import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { formValueSelector, getFormValues } from "redux-form";

import { analyticsStoreEvent } from "@/mk2/actions";
import type { UtmQuery } from "@/mk2/apps/home/schemas";
import { SIBLING_COUNTRY_PHONE_PREFIX } from "@/mk2/apps/users/constants";
import Signup from "@/mk2/apps/users/containers/Signup/Signup";
import {
    signupLoadTrigger,
    signupPhoneRequestTokenTrigger,
    signupPhoneValidateTokenTrigger,
    signupTrigger,
    signupValidateRegistrationInputsTrigger,
} from "@/mk2/apps/users/containers/Signup/Signup.actions";
import { SIGNUP_FORM_NAME } from "@/mk2/apps/users/containers/Signup/Signup.constants";
import { signupReducer } from "@/mk2/apps/users/containers/Signup/Signup.reducers";
import signupSaga from "@/mk2/apps/users/containers/Signup/Signup.sagas";
import { getSignupPageState } from "@/mk2/apps/users/containers/Signup/Signup.selectors";
import { SignupFormData } from "@/mk2/apps/users/containers/Signup/SignupForm";
import { socialAuthReducer } from "@/mk2/apps/users/containers/SocialAuth/SocialAuth.reducers";
import { socialAuthSaga } from "@/mk2/apps/users/containers/SocialAuth/SocialAuth.sagas";
import { signupUrl } from "@/mk2/apps/users/urls";
import { loginSignupTabs } from "@/mk2/apps/users/utils";
import { SubmitButton } from "@/mk2/components/forms/SubmitButton";
import { FormPageType } from "@/mk2/constants/enums";
import { AppName } from "@/mk2/containers/Application/Application";
import { PhotosUploadConfig } from "@/mk2/containers/PhotosUpload/PhotosUpload";
import { photosUploadTrigger } from "@/mk2/containers/PhotosUpload/PhotosUpload.actions";
import {
    mkReduxFormMapDispatchToProps,
    mkReduxFormMapStateToProps,
} from "@/mk2/decorators/mkReduxForm";
import { memoizeOne } from "@/mk2/helpers/cache";
import { cachedInitialValues } from "@/mk2/helpers/form";
import { PhotoUploadStatus, SuccessfulPhotoUpload } from "@/mk2/helpers/form.reducers";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { GetParams, url } from "@/mk2/helpers/urls";
import FormPage, { FormPageDispatchProps, FormPageStateProps } from "@/mk2/pages/FormPage";
import { PageLayout, PageRouteProps } from "@/mk2/pages/Page";
import {
    AppState,
    getRequestDeviceMobile,
    getRequestLastKnownUsername,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import {
    ContentItemEntity,
    ContentItemSchema,
    HeaderMenuItem,
    MenuSelection,
    PhotoEntity,
    PhotoSchema,
    UserEntity,
} from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { Store } from "@/mk2/store/configureStore";
import { SEO_TITLE_SIGNUP, SIGNUP_CONFIRM_AND_SIGN_UP } from "@/mk/autogenerated/translation";

export type QueryParams = UtmQuery & {
    next?: string;
    ci?: string;
    username?: string;
    email?: string;
    dueDate?: string;
};

interface OwnProps {}

interface StateProps extends FormPageStateProps {
    query: QueryParams;
    initialValues: SignupFormData;
    currentValues: SignupFormData;
    isMobile: boolean;
    avatar?: PhotoEntity;
    requestUser: UserEntity;
    loadingState: LoadingState;
    contentItemsKey: string;
    contentItemBottom: ContentItemEntity;
    contentItemTop: ContentItemEntity;
}

interface DispatchProps extends FormPageDispatchProps {
    onLoad(contentItemsKey: string, location: HistoryLocationWithState);
    onSignup(values: SignupFormData, query: QueryParams, location: HistoryLocationWithState);
    onStoreEvent(name: string, props: any);
    onValidateInputs(
        values: SignupFormData,
        query: QueryParams,
        location: HistoryLocationWithState,
    );
    onPhoneVerificationRequestToken(
        countryPrefix: string,
        rawPhoneNumber: string,
        resend: boolean,
        location: HistoryLocationWithState,
    );
    onPhoneVerificationValidateToken(
        token: string,
        fullPhoneNumber: string,
        location: HistoryLocationWithState,
    );
    onPrepareUploadPhotos(
        formName: string,
        config: PhotosUploadConfig,
        files: File[],
        processPhotoUploadOnFormPage: FormPageType,
        accessKey?: string,
    );
}

type RouteProps = PageRouteProps<{}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class SignupPage extends FormPage<OwnProps & RouteProps, StateProps, DispatchProps> {
    private parseQuery = memoizeOne((qs: string): QueryParams => {
        // parse and sanitize input
        return parse(qs) as QueryParams;
    });

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const query = this.parseQuery(ownProps.location.search);
            const username = query.username
                ? (query.username as string).toLowerCase()
                : getRequestLastKnownUsername(state);

            const contentItemsKey = query.ci as string;
            const signupPageState = getSignupPageState(state, contentItemsKey);
            const loadingState = contentItemsKey
                ? signupPageState.loadingState
                : LoadingState.SUCCESS;
            const contentItemTop = isSuccess(loadingState)
                ? getDenormalizedEntity<ContentItemEntity>(
                      state,
                      ContentItemSchema,
                      signupPageState.contentItemTop,
                  )
                : undefined;
            const contentItemBottom = isSuccess(loadingState)
                ? getDenormalizedEntity<ContentItemEntity>(
                      state,
                      ContentItemSchema,
                      signupPageState.contentItemBottom,
                  )
                : undefined;

            // initial form values
            const initialEmail = (query.email as string) || "";
            const initialUsername = initialEmail
                ? initialEmail.split("@")[0].replace(/[^0-9a-z_]/g, "")
                : "";
            const initialDueDate = (query.dueDate as string) || "";
            const initialPregnantState =
                Object.keys(query).includes("pregnant") || initialDueDate ? "1" : "";

            const selector = formValueSelector(SIGNUP_FORM_NAME);
            const currentPhotos = (selector(state, "photos") as any[]) || [];
            const avatar: PhotoEntity =
                currentPhotos &&
                currentPhotos.length > 0 &&
                currentPhotos[0].status === PhotoUploadStatus.SUCCESS
                    ? getDenormalizedEntity<PhotoEntity>(
                          state,
                          PhotoSchema,
                          (currentPhotos[0] as SuccessfulPhotoUpload).photoId,
                      )
                    : null;

            return {
                ...mkReduxFormMapStateToProps(SIGNUP_FORM_NAME)(state),

                avatar,
                query,
                requestUser: getRequestUser(state),
                initialValues: cachedInitialValues(
                    SIGNUP_FORM_NAME,
                    {
                        username: initialUsername,
                        password: "",
                        email: initialEmail,
                        place: "",
                        locationCounty: "",
                        pregnancyState: initialPregnantState,
                        dueDate: initialDueDate,
                        countryPrefix: SIBLING_COUNTRY_PHONE_PREFIX,
                        rawPhoneNumber: "",
                        fullPhoneNumber: "",
                        token: "",
                        photos: [],
                        currentPhoto: false,
                        visibleFace: false,
                        alonePhoto: false,
                        // avatarCrop: [xPosition, yPosition, width]
                        avatarCrop: [0, 0, 100],
                    },
                    username,
                ),
                isMobile: getRequestDeviceMobile(state),
                currentValues: getFormValues(SIGNUP_FORM_NAME)(state) as SignupFormData,
                loadingState,
                contentItemsKey,
                contentItemTop,
                contentItemBottom,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            ...mkReduxFormMapDispatchToProps(SIGNUP_FORM_NAME),
            onLoad: signupLoadTrigger,
            onSignup: signupTrigger,
            onStoreEvent: analyticsStoreEvent,
            onValidateInputs: signupValidateRegistrationInputsTrigger,
            onPhoneVerificationRequestToken: signupPhoneRequestTokenTrigger,
            onPhoneVerificationValidateToken: signupPhoneValidateTokenTrigger,
            onPrepareUploadPhotos: photosUploadTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_SIGNUP;
    }

    public render(props: Props): false | JSX.Element {
        return <Signup {...props} />;
    }

    public hasPageHeaderPart1(props: Props): boolean {
        const { isMobile } = props;
        if (isMobile) {
            return true;
        }
    }

    public hasHeaderBackButton(props: Props): boolean {
        const { currentFormPage } = props;
        if (currentFormPage !== FormPageType.Main) {
            return true;
        }
    }

    public getDefaultBackPath(props: Props): string {
        return url(signupUrl, {});
    }

    public getPageLayout(app: AppName, props: Props): PageLayout {
        return PageLayout.MainContent;
    }

    public getHeaderTabs(props: Props): JSX.Element[] | null {
        const { isMobile, currentFormPage, query } = props;
        if (isMobile && currentFormPage === FormPageType.Main) {
            return loginSignupTabs("signup", query as GetParams);
        }
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: app,
            activeItem: HeaderMenuItem.UserSignup,
        };
    }

    public hasMobileLogo(props: Props): boolean {
        const { avatar } = props;
        if (!avatar) {
            return true;
        }
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const { currentFormPage, avatar, isMobile } = props;
        if (currentFormPage === FormPageType.SignupPhotoUpload && avatar && isMobile) {
            return this.getSubmitButton(props);
        }

        return null;
    }

    public getSubmitButton(props: Props): React.ReactElement {
        return <SubmitButton label={SIGNUP_CONFIRM_AND_SIGN_UP} formName={SIGNUP_FORM_NAME} />;
    }

    public getFooter(props: Props): boolean {
        return null;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.users.signup", signupReducer);
        store.injectSaga("containers.users.signup", signupSaga);

        // Social auth (shared between signup and login pages)
        store.injectReducer("containers.users.socialAuth", socialAuthReducer);
        store.injectSaga("containers.users.socialAuth", socialAuthSaga);
    }

    public getMetaRobots(props: Props): string {
        if (props.location.search !== "") {
            return "noindex,nofollow";
        }

        return super.getMetaRobots(props);
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        return baseUrl + signupUrl;
    }
}
